import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { getDoctorByUserId } from '@/services/doctors';
import type { UserDTO } from '@/types/auth';
import { UserRole } from '@/types/auth';
import { normalizeAuthRole } from '@/lib/postAuthSession';
import {
  DOCTOR_ONBOARDING_CACHE_KEY,
  doctorNeedsOnboarding,
} from '@/lib/doctorOnboarding';

type DoctorOnboardingGateProps = {
  user: UserDTO | null;
  children: ReactNode;
};

const CHECKLIST_PATH = '/onboarding/doctor-checklist';

export function DoctorOnboardingGate({ user, children }: DoctorOnboardingGateProps) {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [shouldShowChecklist, setShouldShowChecklist] = useState(false);
  const [checked, setChecked] = useState(false);
  const isDoctor = normalizeAuthRole(user?.role) === UserRole.DOCTOR;

  useEffect(() => {
    let isMounted = true;

    const loadStatus = async () => {
      if (!user?.id || !isDoctor) {
        setChecked(true);
        return;
      }

      const cacheKey = DOCTOR_ONBOARDING_CACHE_KEY(user.id);
      const cached = localStorage.getItem(cacheKey);
      if (cached === 'false') {
        setShouldShowChecklist(false);
        setChecked(true);
        return;
      }
      if (cached === 'true') {
        setShouldShowChecklist(true);
        setChecked(true);
        return;
      }

      setIsLoading(true);
      try {
        const response = await getDoctorByUserId(user.id);
        if (!isMounted) return;

        const needs = doctorNeedsOnboarding(
          response.success ? response.data : null,
        );
        setShouldShowChecklist(needs);
        localStorage.setItem(cacheKey, String(needs));
      } catch (err) {
        if (!isMounted) return;
        console.warn('[DoctorOnboardingGate] Erro ao verificar onboarding:', err);
        setShouldShowChecklist(true);
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setChecked(true);
        }
      }
    };

    setChecked(false);
    void loadStatus();

    return () => {
      isMounted = false;
    };
  }, [user?.id, isDoctor]);

  if (!user || !isDoctor) {
    return <>{children}</>;
  }

  if (isLoading || !checked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (shouldShowChecklist && location.pathname !== CHECKLIST_PATH) {
    return <Navigate to={CHECKLIST_PATH} replace />;
  }

  if (!shouldShowChecklist && location.pathname === CHECKLIST_PATH) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
